/* eslint-disable class-methods-use-this */
import * as braze from '@braze/web-sdk';
import { AnalyticsUserData } from '../types';
import BaseTracker, { PlatformProperty } from './BaseTracker';
import { BrazePlugin } from '../../../app/scripts/modules/CordovaPlugins.types';

/**
 * Class for connectivity to Braze for user communications (in-app messaging, push notifications).
 */
export class BrazeTracker extends BaseTracker {
  #brazeKey: string;
  #contentCardCallbackFn: any;

  brazeSdk: BrazePlugin | typeof braze;
  private brazeId;
  private acceptedEventsList = [
    'braze_test_event',
    'Show Education Modal',
    'client_proprofile_tab_clicked',
  ];

  public isInitialized = false;

  constructor(
    env: string,
    platform: PlatformProperty,
    brazeKey: string,
    isApp?: boolean,
    contentCardCallbackFn?: any,
  ) {
    super('braze', 'Braze', env, platform, isApp);
    this.#brazeKey = brazeKey;
    this.brazeSdk = this.isApp ? window.BrazePlugin : braze;
    this.isInitialized = false;
    this.#contentCardCallbackFn = contentCardCallbackFn;
  }

  initializeBraze() {
    if (!this.isInitialized) {
      // The cordova plugin for Braze is initialized on app startup, and does not contain the same
      // initialization functions around IAMs, as they should work out of the box. The web SDk
      // isn't initialized until the command is explicitly called, and needs the associated IAM
      // functions. Additionally, the content card functions are different between the web sdk and
      // their cordova plugin
      if (!this.isApp) {
        braze.initialize(this.#brazeKey, {
          baseUrl: 'sdk.iad-05.braze.com',
          enableLogging: true,
          allowUserSuppliedJavascript: true,
          minimumIntervalBetweenTriggerActionsInSeconds: 0,
        });
        braze.automaticallyShowInAppMessages();
        braze.subscribeToInAppMessage(inAppMessage => {
          braze?.showInAppMessage(inAppMessage);
        });
      } else {
        braze.initialize(this.#brazeKey, {
          baseUrl: 'sdk.iad-05.braze.com',
        });
      }
      braze.subscribeToContentCardsUpdates(
        this.#contentCardCallbackFn,
      );
      braze.requestContentCardsRefresh();
      this.isInitialized = true;
    }
  }

  /**
   * Logs the user's current device with Braze.
   * @global window.AppboyPlugin The Braze SDK. Only loaded in the mobile app.
   * @param userData Data from the analytics package that identifies the current user.
   */
  updateUser(userData: AnalyticsUserData) {
    const {
      client_handle, is_logged_in, user_token,
    } = userData;
    const brazeUserId = is_logged_in ? user_token : client_handle;

    if (brazeUserId) {
      if (brazeUserId !== this.brazeId) {
        this.initializeBraze();
        this.brazeSdk?.changeUser(brazeUserId);
        this.brazeId = brazeUserId;
        if (this.isApp) {
          braze.changeUser(brazeUserId);
        } else {
          braze.openSession();
        }
        braze.requestContentCardsRefresh();
        this.#contentCardCallbackFn(braze.getCachedContentCards());
      }
    }
  }

  /**
   * Registers custom event with Braze
   * @param eventName name of event to be sent to braze
   * @param properties data to be sent in the Braze custom event
   */
  trackEvent(eventName: string, properties?: object) {
    const eventProperties = {
      external_id: this.brazeId,
      ...properties,
    };

    if (this.acceptedEventsList.includes(eventName)) {
      this.brazeSdk?.logCustomEvent(eventName, eventProperties);
    }
  }

  isPushBlocked() {
    return this.brazeSdk?.isPushBlocked();
  }

  isPushSupported() {
    return this.brazeSdk?.isPushSupported();
  }

  isPushPermissionGranted() {
    return this.brazeSdk?.isPushPermissionGranted();
  }

  requestPushPermission() {
    return this.brazeSdk?.requestPushPermission();
  }

  openSession() {
    return braze.openSession();
  }
}
